// @flow
import * as R from 'ramda';

import {
  BILLING_V1_URL,
  BILLING_V1_C5_URL,
  BILLING_V1_STACKSVAULT_URL,
  BILLING_V1_ADKONUNIT_URL,
  BILLING_V1_UNIVERMAG_URL,
} from 'settings/App';

const SF_MANUAL_LINK = process.env.REACT_APP_SF_MANUAL_LINK || '';
const SF_WHITE_HAT_MANUAL_LINK = process.env.REACT_APP_SF_WHITE_HAT_MANUAL_LINK || '';
const SF_TERMS_LINK = process.env.REACT_APP_SF_TERMS_LINK || '';
const WL_MANUAL_LINK = process.env.REACT_APP_WL_MANUAL_LINK || '';

type WLConfig = {|
  id: string,
  domains: RegExp[],
  title: string,
  siteName: string,
  logotype: {
    main: string,
    big: string,
    favicon: string,
  },
  helpMenu: {
    show: boolean,
    links: {
      manual: (isBlackHatMode?: boolean) => string,
      terms: string,
    },
  },
  billingApiUrl: string,
  isDefault: boolean,
  hideBuyProfiles: boolean,
  showIntercom: boolean,
  showSignUp: boolean,
  showContactForm: boolean,
  showFirstLoginNotification: boolean,
  hasLanding: boolean,
|};

type WLOptions = {
  title?: string,
  logotype?: {
    main?: string,
    big?: string,
    favicon?: string,
  },
  helpMenu?: {
    show?: boolean,
    links?: {
      manual?: (isBlackHatMode?: boolean) => string,
      terms?: string,
    },
  },
  billingApiUrl?: string,
  isDefault?: boolean,
  showIntercom?: boolean,
  showSignUp?: boolean,
  showContactForm?: boolean,
  showFirstLoginNotification?: boolean,
  hideBuyProfiles?: boolean,
  hasLanding?: boolean,
};

export class WhiteLabel {
  configs: WLConfig[] = [];
  defaultConfig: ?WLConfig = null;
  currentConfig: ?WLConfig = null;

  constructor(configs: WLConfig[]) {
    this.configs = configs;
    this.defaultConfig = configs.find((i) => i.isDefault);

    if (window && window.location.hostname) {
      this.currentConfig = this.getConfigByDomain(window.location.hostname);
    }

    if (!this.currentConfig) {
      this.currentConfig = this.defaultConfig;
    }
  }

  static generate(id: string, siteName: string, domains: RegExp[], options: WLOptions): WLConfig {
    const {
      title,
      logotype,
      helpMenu,
      isDefault,
      showIntercom,
      showSignUp,
      showContactForm,
      showFirstLoginNotification,
      billingApiUrl,
      hideBuyProfiles,
      hasLanding,
    } = options;

    const { main: mainLogotype, big: bigLogotype, favicon } = logotype || {};

    const { show: showHelpMenu, links: linksHelpMenu } = helpMenu || {};

    const { manual: manualLink, terms: termsLink } = linksHelpMenu || {};

    return {
      id,
      domains,
      billingApiUrl: billingApiUrl || '',
      title: title || siteName,
      siteName,
      logotype: {
        main: mainLogotype || '',
        big: bigLogotype || '',
        favicon: favicon || '',
      },
      helpMenu: {
        show: showHelpMenu || false,
        links: {
          manual: manualLink || (() => ''),
          terms: termsLink || '',
        },
      },
      isDefault: isDefault || false,
      showIntercom: showIntercom || false,
      hideBuyProfiles: hideBuyProfiles || false,
      showSignUp: showSignUp || false,
      showContactForm: showContactForm || false,
      showFirstLoginNotification: showFirstLoginNotification || false,
      hasLanding: (process.env.NODE_ENV === 'development' ? false : hasLanding) || false,
    };
  }

  getConfigById(id: string): ?WLConfig {
    if (!id) {
      throw new Error('Get WL config by undefined ID');
    }

    return this.configs.find((i) => i.id === id);
  }

  getConfigByDomain(domain: string): ?WLConfig {
    if (!domain) {
      throw new Error('Get WL config by undefined domain');
    }

    return this.configs.find((i) => i.domains.reduce((acc, j) => acc || j.test(domain), false));
  }

  getConfig(): ?WLConfig {
    return this.currentConfig;
  }

  get(name: string) {
    return this.currentConfig ? R.prop(name, this.currentConfig) : undefined;
  }

  getIn(path: mixed[]) {
    return this.currentConfig ? R.path(path, this.currentConfig) : undefined;
  }
}

const configs = [
  WhiteLabel.generate(
    'sofarmme',
    'Sofarm.me',
    [/^sofarm\.(me|local)$/, /^dev[0-9]*\.s-frm\.net$/, /^dev[0-9]*\.int\.s-frm\.net$/],
    {
      title: 'Soltexfarm',
      helpMenu: {
        show: true,
        links: {
          manual: (isBlackHatMode) => (isBlackHatMode ? SF_MANUAL_LINK : SF_WHITE_HAT_MANUAL_LINK),
          terms: SF_TERMS_LINK,
        },
      },
      showIntercom: true,
      showSignUp: true,
      isDefault: true,
      showContactForm: true,
      showFirstLoginNotification: true,
      billingApiUrl: BILLING_V1_URL,
      hasLanding: true,
    },
  ),
  WhiteLabel.generate(
    'sfrmnet',
    's-frm.net',
    [
      /^s-frm\.net$/,
      /^sfrmnet\.dev[0-9]*\.s-frm\.net$/,
      /^sfrmnet\.dev[0-9]*\.int\.s-frm\.net$/,
      /^sfrmnet\.stage\.sofarm.me$/,
    ],
    {
      title: 'Sofarm',
      helpMenu: {
        show: true,
        links: {
          manual: (isBlackHatMode) => (isBlackHatMode ? SF_MANUAL_LINK : SF_WHITE_HAT_MANUAL_LINK),
          terms: SF_TERMS_LINK,
        },
      },
      showFirstLoginNotification: true,
      billingApiUrl: BILLING_V1_URL,
    },
  ),
  WhiteLabel.generate(
    'c5',
    'c5.sofarm.me',
    [
      /^c5.sofarm\.(me|local)$/,
      /^c5\.dev[0-9]*\.s-frm\.net$/,
      /^c5\.dev[0-9]*\.int\.s-frm\.net$/,
      /^c5\.stage\.sofarm.me$/,
    ],
    {
      title: 'Interactive',
      helpMenu: {
        show: true,
        links: {
          manual: () => WL_MANUAL_LINK,
        },
      },
      billingApiUrl: BILLING_V1_C5_URL,
    },
  ),
  WhiteLabel.generate(
    'stacksvault',
    'stacksvault.com',
    [
      /^stacksvault.com$/,
      /^stacksvault\.dev[0-9]*\.s-frm\.net$/,
      /^stacksvault\.dev[0-9]*\.int\.s-frm\.net$/,
      /^stacksvault\.stage\.sofarm.me$/,
    ],
    {
      title: 'Stacksvault',
      helpMenu: {
        show: true,
        links: {
          manual: () => WL_MANUAL_LINK,
        },
      },
      billingApiUrl: BILLING_V1_STACKSVAULT_URL,
    },
  ),
  WhiteLabel.generate(
    'adkonunit',
    'adkonunit.com',
    [
      /^adkonunit.(com|local)$/,
      /^adkonunit\.dev[0-9]*\.s-frm\.net$/,
      /^adkonunit\.dev[0-9]*\.int\.s-frm\.net$/,
      /^adkonunit\.stage\.sofarm.me$/,
    ],
    {
      title: 'Adkonunit',
      helpMenu: {
        show: false,
      },
      hideBuyProfiles: true,
      billingApiUrl: BILLING_V1_ADKONUNIT_URL,
    },
  ),

  WhiteLabel.generate(
    'univermag',
    'fbblack.com',
    [
      /^fbblack.(com|local)$/,
      /^fbblack\.dev[0-9]*\.int\.s-frm\.net$/,
      /^fbblack\.stage\.sofarm.me$/,
    ],
    {
      title: 'Univer-Mag',
      showSignUp: false,
      showIntercom: false,

      helpMenu: {
        show: true,
        links: {
          manual: () => WL_MANUAL_LINK,
        },
      },
      billingApiUrl: BILLING_V1_UNIVERMAG_URL,
    },
  ),
];

export default new WhiteLabel(configs);



// WEBPACK FOOTER //
// ./src/settings/WhiteLabel.js